// @ts-check
import React, { useState } from 'react'
import { Drawer, Dropdown, Menu } from 'antd'
import Select from 'react-select'
import { AiOutlineDown } from 'react-icons/ai'

import { usePet, useToggle } from '../../modules'
import { useSelector } from 'react-redux'

import Confirm from '../reusable/confirm'
import EditPermissions from '../modals/editPermissions'
import pluralize from 'pluralize'
import { getStationSelectOptions } from '../../utils/stationOptions'

const EditRole = ({ role, toggle, visible, onPerform }) => {
    const { user } = useSelector(state => state.application)
    const [name, setName] = useState(role?.name)
    const [selected, setSelected] = useState()
    const [allStores, setAllStores] = useState([])
    const [stations, setStations] = useState(role?.stations ? role?.stations?.map(stat => ({ type: 'station', label: stat?.name?.replace(stat?.name[0], stat?.name[0].toUpperCase()), value: stat?.name, permissions: stat?.permissions?.map(prm => ({ label: prm, value: prm })) ?? [] })) : [])
    const [stores, setStores] = useState((role?.stores ?? []).map(st => ({
        label: st.name,
        value: st.store,
        permissions: (st.permissions ?? []).map(prm => ({ label: prm, value: prm }))
    })))

    const [store, setStore] = useState(false)

    // stores: stores.map(store => ({
    //     name: store.label,
    //     store: store.value,
    //     permissions: store.permissions.map(prm => prm.value)
    // }))
    const [permissionsModal, togglePermissionsModal] = useToggle()

    const setPermissions = (val, i, st = false) => {
        setStore(st)
        setSelected({ subject: val, i })
        togglePermissionsModal()
    }

    const close = () => {
        toggle()
    }

    const onComplete = ({ data }, notif, ops) => {
        onPerform(data)
        notif('User has been added', ops('success'))
        
        setTimeout(close, 300)
    }

    const onReapplyComplete = ({ data }, notif, ops) => {
        const updated = Number(data?.updated ?? 0)
        notif(`Re-applied this role to ${updated} ${pluralize('user', updated)}`, ops('success'))
        onPerform(data)
    }

    usePet('stores', { limit: 100 }, { notify: true, callback: async ({ data }) => { 
        setAllStores(data?.stores?.map(store => ({
            type: 'store',
            label: store.name,
            value: store._id,
            permissions: []
        })))
    } })

    let hasStore = stations.some(station => station?.value === 'store')

    const changeStations = strs => {
        setStations(strs)

        if (!strs.some(station => station.value === 'store'))
             setStores([])
    }

    const changePermissions = (i, permissions, type) => {
        if (type === 'station') {
            let temp = [...stations]
            temp[i].permissions = permissions
            setStations(temp)
        } else {
            let temp = [...stores]
            temp[i].permissions = permissions
            setStores(temp)
        }
        
        setSelected()
    }

    const [{ loading }, execute] = usePet('editUserRole', {
        id: role?._id,
        name,
        stations: stations.map(station => ({
            name: station.value,
            permissions: station.permissions.map(prm => prm.value)
        })),
        stores: stores.map(store => ({
            name: store.label,
            store: store.value,
            permissions: store.permissions.map(prm => prm.value)
        }))
    }, { notify: true, callback: onComplete, manual: true })

    const [{ loading: reapplyLoading }, reapplyRole] = usePet('reapplyUserRole', {
        id: role?._id,
    }, { notify: true, callback: onReapplyComplete, manual: true })

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button
                        disabled={ reapplyLoading }
                        onClick={() => Confirm({
                            title: 'Re-apply role to existing users',
                            message: 'This will refresh station and store access for all users currently assigned this role.',
                            onOk: reapplyRole,
                            okText: 'Re-apply Role',
                        })}
                        className="text-white hisani-btn-orange-light mr-2 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                        Re-apply to Users
                    </button>
                    <button disabled={ loading } onClick={execute} className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save Changes</button>
                </div>
            }
            width={600}>
            <p className="font-extrabold text-3xl mb-0">Edit User Role</p>
            <div className="mt-3 mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Role changes apply to the role definition going forward. Existing staff who already have this role may need their user account re-saved before new station/store access shows up for them.
            </div>
            <div className="divide-y divide-light-gray-400">
                {
                    permissionsModal &&
                    <EditPermissions
                        store={ store }
                        item={ selected }
                        onPerform={ changePermissions }
                        visible={ permissionsModal }
                        toggle={ togglePermissionsModal } />
                }
                <div>
                    <div>
                        <div className="flex flex-wrap mt-3 -mx-3">
                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                    Role Name
                                </label>
                                <input 
                                    onChange={ e => setName(e.target.value) }
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" 
                                    id="grid-role-name" 
                                    type="text" 
                                    value={ name }
                                    name="name"
                                    placeholder="Nurse-In-Charge" />
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <p className="font-bold my-3">Stations</p>
                    <Select
                        isMulti
                        value={ stations }
                        onChange={ changeStations }
                        options={ getStationSelectOptions(user?.entitledModules) }
                        placeholder="Set Stations" />
                    <br />
                    {
                        stations?.length > 0 ?
                            <table className="table-auto w-full">
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">No</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Name</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Permissions</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center"></div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        stations.map((station, key) => {
                                            let hasAll = station.permissions.some(prm => prm.label === '*')

                                            return (
                                                <tr>
                                                    <td className="p-2">
                                                        <div className="text-center">{key + 1}.</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{ station.label }</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{ hasAll ? 'All Permissions' : `${station.permissions.length} ${pluralize('permissions', station.permissions.length)}` }</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div>
                                                            <Dropdown
                                                                overlay={
                                                                    <Menu>
                                                                        <Menu.Item onClick={ () => setPermissions(station, key) }>View</Menu.Item>
                                                                    </Menu>
                                                                }>
                                                                <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                            </Dropdown>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                        )
                                    }
                                </tbody>
                            </table>
                        : 
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">Please click above to add a new role</p>
                            </div>
                    }
                </div>
                {
                    hasStore &&
                    <div>
                        <p className="font-bold my-3">Stores</p>
                        <Select
                            isMulti
                            value={ stores }
                            onChange={ setStores }
                            options={ allStores }
                            placeholder="Set Stores" />
                        <br />
                        {
                            stores?.length > 0 ?
                                <table className="table-auto w-full">
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">No</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Store</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Permissions</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center"></div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            stores.map((store, key) =>
                                                <tr>
                                                    <td className="p-2">
                                                        <div className="text-center">{key + 1}.</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{ store.label }</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{ store.permissions.some(prm => prm?.label === '*') ? 'All Permissions' : `${store.permissions.length} ${pluralize('permissions', store.permissions.length)}` }</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div>
                                                            <Dropdown
                                                                overlay={
                                                                    <Menu>
                                                                        <Menu.Item onClick={ () => setPermissions(store, key, true) }>View</Menu.Item>
                                                                    </Menu>
                                                                }>
                                                                <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                            </Dropdown>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            : 
                                <div className="w-full h-16 flex items-center justify-center">
                                    <p className="font-semibold text-base">Please click above to add a new role</p>
                                </div>
                        }
                    </div>
                }
            </div>
        </Drawer>
    )
}

export default EditRole
